/**
 * Debridement workflow configuration — FRONTEND OWNED.
 *
 * Every question and every answer lives here rather than coming from
 * `optionTypeAsync` / the .NET option-type API. That is a deliberate departure
 * from how the other Treatment sections work, and it is what makes this section
 * paint and function offline.
 *
 * ── THE ONE RULE THAT MATTERS ───────────────────────────────────────────────
 * Owning the LIST does not mean owning the CONTRACT. Every `key` below is
 * persisted by .NET and read back on load. A key changed here is a saved record
 * that no longer round-trips. Labels are free to change; keys are not.
 *
 * Adding an option = client release. That is the cost of offline. If an option
 * list starts changing faster than the release train, move that one list back
 * to the API — the shape here is deliberately the same `{ key, value }` the
 * SelectBox/MultiSelect primitives already consume, so a single list can be
 * swapped back without touching the components.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Source of truth for content: "NE1 Debridement Tree V1" (June 2026).
 * Answer options are carried over verbatim. Only the STRUCTURE differs —
 * prerequisites are separated from the procedure record and made non-terminal.
 */

/* ── config version ───────────────────────────────────────────────────────
 * Sent with every save. Lets the API tell which client build produced a
 * record, which is the only way to interpret historical data after an option
 * list changes. Bump on ANY key change in this file.
 * ------------------------------------------------------------------------ */
export const DEBRIDEMENT_CONFIG_VERSION = "1.0.0";

/* ── care settings ────────────────────────────────────────────────────────
 * Keys match CUSTOMER_GROUP_NAME in src/constants/index.js.
 * Read from detailViewData.customerGroup — see getCareSetting() below.
 * ------------------------------------------------------------------------ */
export const CARE_SETTINGS = {
  hospital: "Hospital",
  criticalAccess: "Critical access",
  rehabhospitalLTAC: "Rehab hospital/ LTAC", //NA
  nursingHome: "Nursing Home",
  homeHealthAgency: "Home health agency",
  hospice: "Hospice",
  paceProgram: "Pace Program",
  woundcare: "Wound Care Center",
  podiatry: "Podiatry",
};

/** Fallback when customerGroup is absent. The most restrictive setting is the
 *  safe default: it shows every prerequisite rather than hiding one. */
export const DEFAULT_CARE_SETTING = "hospital";

export const getCareSetting = (detailViewData) => {
  const group = detailViewData?.customerGroup;
  return group && CARE_SETTINGS[group] ? group : DEFAULT_CARE_SETTING;
};

/* ── prerequisite policy ──────────────────────────────────────────────────
 * R  = Required                — a "no" flags the record (High)
 * Rx = RequiredWithOverride    — a documented reason satisfies it (Medium)
 * O  = Optional                — shown, not enforced
 * null = Hidden                — not rendered; its absence is NOT a gap
 *
 * UNVALIDATED. Structural proposal pending clinical/compliance sign-off.
 * ------------------------------------------------------------------------ */
const P = { R: "R", Rx: "Rx", O: "O" };

export const PREREQUISITES = [
  {
    key: "consent",
    label: "Was verbal explanation of procedure given and consent received?",
    control: "tristate",
    policy: {
      hospital: P.R,
      criticalAccess: P.R,
      rehabhospitalLTAC: P.R,
      nursingHome: P.R,
      homeHealthAgency: P.R,
      hospice: P.R,
      paceProgram: P.R,
      woundcare: P.R,
      podiatry: P.R,
    },
    settingNotes: {
      nursingHome: "Care plan consent plus verbal; responsible party / POA path applies.",
      homeHealthAgency: "Consent-to-treat at admission plus verbal at visit.",
    },
    reasons: [
      { key: "surrogateUnavailable", value: "Patient unable to consent — surrogate unavailable" },
      { key: "onFileNotReobtained", value: "Consent on file, verbal not re-obtained" },
      { key: "declinedToSign", value: "Patient declined to sign" },
      { key: "emergent", value: "Emergent procedure" },
      { key: "notDocumented", value: "Not documented at time of care" },
    ],
  },
  {
    key: "performedBy",
    label: "Debridement performed by",
    control: "select",
    policy: {
      hospital: P.R,
      criticalAccess: P.R,
      rehabhospitalLTAC: P.R,
      nursingHome: P.R,
      homeHealthAgency: P.R,
      hospice: P.R,
      paceProgram: P.R,
      woundcare: P.R,
      podiatry: P.R,
    },
    settingNotes: {},
    // Keys preserve the existing slug format already persisted by the API.
    options: [
      { key: "mddodpmanppaproviderresident", value: "MD, DO, DPM, ANP, PA, Provider Resident" },
      { key: "ptorrn-certified(cwswccwocn)", value: "PT or RN – Certified (CWS, WCC, WOCN, OMS)" },
      { key: "pt", value: "PT" },
    ],
    reasons: [],
  },
  {
    key: "providerOrder",
    label: "Is there a provider order?",
    control: "tristate",
    policy: {
      hospital: P.R,
      criticalAccess: P.Rx,
      rehabhospitalLTAC: P.Rx,
      nursingHome: P.Rx,
      homeHealthAgency: P.R,
      hospice: P.R,
      paceProgram: P.R,
      woundcare: P.Rx,
      podiatry: null, // provider-performed by definition — nobody to order it
    },
    settingNotes: {
      // hospital: "Not required when the provider debrides under their own licence.",
      homeHealthAgency: "Must sit on the physician-established plan of care.",
      hospice: "Per plan of care.",
    },
    reasons: [
      { key: "verbalPendingCountersign", value: "Verbal order — countersign pending" },
      { key: "pendingProviderReview", value: "Order pending provider review" },
      { key: "standingProtocol", value: "Standing protocol" },
      { key: "providerUnreachable", value: "Unable to reach provider" },
      { key: "emergentOrderToFollow", value: "Emergent — order to follow" },
    ],
  },
  {
    key: "painBefore",
    label: "Pain scale obtained right before procedure",
    control: "pain",
    policy: {
      hospital: P.R,
      criticalAccess: P.Rx,
      rehabhospitalLTAC: P.Rx,
      nursingHome: P.Rx,
      homeHealthAgency: P.Rx,
      hospice: P.R,
      paceProgram: P.Rx,
      woundcare: P.Rx,
      podiatry: P.Rx,
    },
    settingNotes: {
      hospice: "Comfort is the primary endpoint in hospice, not a side measure.",
    },
    reasons: [
      { key: "unableToSelfReport", value: "Patient unable to self-report" },
      { key: "patientDeclined", value: "Patient declined" },
      { key: "notObtainedEmergent", value: "Not obtained — emergent" },
      { key: "nonverbal", value: "Nonverbal / cognitively impaired" },
    ],
  },
  {
    key: "photoPre",
    label: "Was a pre-debridement photo obtained?",
    control: "photo",
    policy: {
      hospital: P.R,
      criticalAccess: P.Rx,
      rehabhospitalLTAC: P.Rx,
      nursingHome: P.Rx,
      homeHealthAgency: P.O,
      hospice: P.O,
      paceProgram: P.O,
      woundcare: P.Rx,
      podiatry: P.Rx,
    },
    settingNotes: {
      homeHealthAgency: "Visit cadence cannot meet a fixed 4-day window — recency is visit-relative here.",
      hospice: "Visit-relative recency.",
      paceProgram: "Visit-relative recency.",
    },
    options: [
      { key: "rightBefore", value: "Right before procedure" },
      { key: "withinWindow", value: "Within recency window" },
    ],
    reasons: [
      { key: "patientDeclined", value: "Patient declined photography" },
      { key: "policyProhibits", value: "Facility / home policy prohibits" },
      { key: "noCompliantDevice", value: "No compliant capture device" },
      { key: "notVisualisable", value: "Wound not visualisable" },
    ],
  },
];

/** V1 specifies "within previous 4 days". That is satisfiable inpatient and
 *  routinely unsatisfiable in the community, so the window varies by setting. */
export const PHOTO_RECENCY_WINDOW = {
  hospital: "48 hours",
  criticalAccess: "48 hours",
  rehabhospitalLTAC: "7 days",
  nursingHome: "7 days",
  homeHealthAgency: "this visit or prior visit",
  hospice: "this visit or prior visit",
  paceProgram: "this visit or prior visit",
  woundcare: "7 days",
  podiatry: "7 days",
};

/* ── Block 0 · router ─────────────────────────────────────────────────── */

export const NOT_PERFORMED_REASONS = [
  { key: "notIndicated", value: "Not clinically indicated" },
  { key: "patientDeclined", value: "Patient / representative declined" },
  { key: "notTolerated", value: "Not tolerated" },
  { key: "deferred", value: "Deferred to next visit" },
  { key: "noOrder", value: "No provider order available" },
];

/* ── Block 2 · procedure — V1 lists, verbatim ─────────────────────────── */

/**
 * Type of debridement. V1's two-level structure is preserved: a type, and for
 * three of the four types an approach beneath it.
 *
 * KNOWN CONTENT DEFECT (carried over deliberately — see the restructure doc
 * §6.1): V1 files "Enzymatic" under Non-selective. Enzymatic debridement is
 * SELECTIVE — that is its defining property. Flagged in the UI for clinical
 * review rather than silently corrected, because the taxonomy is not ours.
 */
export const DEBRIDEMENT_TYPES = [
  {
    key: "surgical",
    value: "Surgical (Open Approach)",
    approachLabel: "Approach",
    approaches: [{ key: "cutaway", value: "Cut Away" }],
  },
  {
    key: "awcm-selectivesharp",
    value: "Active Wound Care Management (Open Approach) — Selective Sharp",
    approachLabel: "Approach",
    approaches: [
      { key: "cutaway", value: "Cut Away" },
      { key: "minorsnip", value: "Minor Snip" },
      { key: "minorpick", value: "Minor Pick" },
      { key: "minorscrape", value: "Minor Scrape" },
      { key: "autolytic", value: "Autolytic - Moist Wound Healing"},
      { key: "mechanical", value: "Mechanical - Scrubbing/Wet to Dry"},
      { key: "chemical", value: "Chemical - Silver Nitrate/Dakins/ Hydrogen Peroxide"}
    ],
  },
  {
    key: "awcm-nonselective",
    value: "Active Wound Care Management (Open Approach) — Non-selective, No Sharps",
    approachLabel: "Approach",
    approaches: [
      { key: "abrasion", value: "Abrasion" },
      { key: "enzymatic", value: "Enzymatic" },
      { key: "wettodry", value: "Wet-to-Dry" },
    ],
    clinicalReviewNote:
      "V1 files Enzymatic under Non-selective. Enzymatic debridement is selective. Carried over as written — pending clinical review.",
  },
  {
    key: "awcm-extraction",
    value: "Active Wound Care Management (Open Approach) — Extraction / Pulling / Stripping",
    approachLabel: null,
    approaches: [],
  },
];

/**
 * Deepest tissue removed. V1's list mixes anatomic layers (Muscle, Bone), a
 * depth descriptor (Partial Thickness) and devitalized material (via the
 * Subcutaneous sub-list). Carried over as written.
 */
export const DEPTH_OF_DEBRIDEMENT = [
  { key: "skin", value: "Skin" },
  { key: "partialthickness", value: "Partial Thickness" },
  { key: "subcutaneoustissue", value: "Subcutaneous Tissue", hasSubList: true },
  { key: "muscle", value: "Muscle" },
  { key: "tendon", value: "Tendon" },
  { key: "bone", value: "Bone" },
  { key: "cartilage", value: "Cartilage" },
  { key: "fascia", value: "Fascia" },
];

/**
 * KNOWN CONTENT DEFECT (restructure doc §6.2): "Slough Tissue Type" here and
 * "Yellow Slough – non-Tissue Type" in NON_TISSUE_REMOVED accept the same
 * clinical answer. Two clinicians can record the same removal differently,
 * which breaks trending. Flagged in the UI, not silently deduplicated.
 */
export const NECROTIC_SUBCUTANEOUS_TISSUE = [
  { key: "sloughtissuetype", value: "Slough Tissue Type" },
  { key: "eschar", value: "Eschar" },
  { key: "fat", value: "Fat" },
  { key: "other", value: "Other" },
];

export const NON_TISSUE_REMOVED = [
  { key: "nails", value: "Nails" },
  { key: "yellowslough-nontissuetype", value: "Yellow Slough – non-Tissue Type" },
  { key: "skingraftorbiologiccovering", value: "Skin Graft or Biologic Covering" },
  { key: "mucosa", value: "Mucosa" },
  { key: "debris", value: "Debris" },
  { key: "biofilm", value: "Biofilm" },
];

export const INSTRUMENTS_USED = [
  { key: "scalpel", value: "Scalpel" },
  { key: "curette", value: "Curette" },
  { key: "scissors", value: "Scissors" },
  { key: "forceps", value: "Forceps" },
  { key: "gauze", value: "Gauze" },
  { key: "cuttingflutetool", value: "Cutting Flute Tool" },
  { key: "norsendebrider", value: "Norsen Debrider" },
  { key: "highpressurewaterjet", value: "High Pressure Water Jet" },
  { key: "hydromechanicalvacveraflo", value: "Hydromechanical V.A.C. VeraFlo" },
  { key: "nailclipper", value: "Nail Clipper" },
  { key: "other", value: "Other" },
];

export const TECHNIQUE_USED = [
  { key: "orsterile", value: "OR Sterile" },
  { key: "bedsidesterile", value: "Bedside Sterile" },
  { key: "bedsideclean", value: "Bedside Clean" },
];

/* ── Block 3 · outcome ────────────────────────────────────────────────── */

export const BLEEDING_AMOUNT = [
  { key: "none", value: "None" },
  { key: "small", value: "Small" },
  { key: "moderate", value: "Moderate" },
  { key: "large", value: "Large" },
];

export const YES_NO = [
  { key: "yes", value: "Yes" },
  { key: "no", value: "No" },
];

/** V1: "0-No Pain -> 10-severe". Same scale before and after. */
export const PAIN_SCALE = Array.from({ length: 11 }, (_, n) => ({
  key: String(n),
  value: n === 0 ? "0 — No pain" : n === 10 ? "10 — Severe" : String(n),
}));

/* ── lookup helper ────────────────────────────────────────────────────── */

/** Resolve a stored key back to its display label. Used by view mode. */
export const labelFor = (list, key) => (list || []).find((o) => o.key === key)?.value ?? key ?? "";

/** Resolve an array of stored keys to display labels. */
export const labelsFor = (list, keys) => (Array.isArray(keys) ? keys : []).map((k) => labelFor(list, k));
